import { useState } from 'react';
import { ORDER_STATUSES } from '@aggregator/shared';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Dialog } from '@/components/ui/dialog';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState, Pagination } from '@/components/ui/states';
import { OrderStatusBadge } from '@/components/OrderStatusBadge';
import { useToast } from '@/components/ui/toast';
import { useOrders, useOrder, useRefundOrder, useApproveOrderPayment, useRejectOrderPayment, useSuppliers, useFetchRecoveryOrders, useAssignManualDelivery, type OrderFilters } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import { formatUsdt, formatDateTime } from '@/lib/utils';

export function OrdersPage() {
  const [filters, setFilters] = useState<OrderFilters>({ page: 1, pageSize: 20 });
  const orders = useOrders(filters);
  const suppliers = useSuppliers();
  const [openId, setOpenId] = useState<string | null>(null);

  const set = (patch: Partial<OrderFilters>) => setFilters((f) => ({ ...f, page: 1, ...patch }));

  return (
    <div>
      <PageHeader title="Orders" description="Every order placed through the bot." />

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
        <Select
          value={filters.status ?? ''}
          onChange={(e) => set({ status: e.target.value || undefined })}
          placeholder="All statuses"
          options={ORDER_STATUSES.map((s) => ({ value: s, label: s }))}
        />
        <Select
          value={filters.supplierId ?? ''}
          onChange={(e) => set({ supplierId: e.target.value || undefined })}
          placeholder="All suppliers"
          options={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
        />
        <Input
          type="date"
          value={filters.from?.slice(0, 10) ?? ''}
          onChange={(e) => set({ from: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
        />
        <Input
          type="date"
          value={filters.to?.slice(0, 10) ?? ''}
          onChange={(e) => set({ to: e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : undefined })}
        />
        <Input
          placeholder="Search user / id"
          value={filters.search ?? ''}
          onChange={(e) => set({ search: e.target.value || undefined })}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {orders.isLoading ? (
            <Loading />
          ) : orders.isError ? (
            <ErrorState error={orders.error} onRetry={() => orders.refetch()} />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>Product</TH>
                  <TH>User</TH>
                  <TH>Supplier</TH>
                  <TH>Total</TH>
                  <TH>Profit</TH>
                  <TH>Status</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {orders.data && orders.data.items.length > 0 ? (
                  orders.data.items.map((o) => (
                    <TR key={o.id}>
                      <TD className="whitespace-nowrap text-muted-foreground">{formatDateTime(o.createdAt)}</TD>
                      <TD>{o.productName}</TD>
                      <TD className="text-muted-foreground">
                        {o.telegramUsername ? `@${o.telegramUsername}` : o.telegramUserId}
                      </TD>
                      <TD className="text-muted-foreground">{o.supplierName}</TD>
                      <TD>{formatUsdt(o.sellingPriceUsdt)}</TD>
                      <TD>{formatUsdt(o.commissionUsdt)}</TD>
                      <TD>
                        <OrderStatusBadge status={o.status} />
                      </TD>
                      <TD className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setOpenId(o.id)}>
                          {o.status === 'verifying_payment' ? 'Review payment' : 'View'}
                        </Button>
                      </TD>
                    </TR>
                  ))
                ) : (
                  <EmptyRow colSpan={8}>No orders match these filters.</EmptyRow>
                )}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {orders.data ? (
        <Pagination
          page={orders.data.page}
          totalPages={orders.data.totalPages}
          onPage={(p) => setFilters((f) => ({ ...f, page: p }))}
        />
      ) : null}

      <OrderDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function OrderDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const order = useOrder(id ?? undefined);
  const refund = useRefundOrder();
  const approvePayment = useApproveOrderPayment();
  const rejectPayment = useRejectOrderPayment();
  const recovery = useFetchRecoveryOrders();
  const assignDelivery = useAssignManualDelivery();
  const { toast } = useToast();
  const [note, setNote] = useState('');

  const o = order.data;
  const canRefund = o && ['completed', 'processing', 'failed'].includes(o.status);
  const canRecover = o?.paymentVerified && ['pending', 'processing', 'failed'].includes(o.status);

  return (
    <Dialog open={Boolean(id)} onClose={onClose} title="Order detail" wide>
      {order.isLoading || !o ? (
        <Loading />
      ) : (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Order id" value={o.id} mono />
            <Info label="Status" value={<OrderStatusBadge status={o.status} />} />
            <Info label="Product" value={o.productName} />
            <Info label="Supplier" value={o.supplierName} />
            <Info label="External order id" value={o.externalOrderId ?? '—'} mono />
            <Info label="Idempotency key" value={o.idempotencyKey} mono />
            <Info label="Supplier cost" value={`${formatUsdt(o.supplierCostUsdt)} USDT`} />
            <Info label="Commission" value={`${formatUsdt(o.commissionUsdt)} USDT`} />
            <Info label="Selling price" value={`${formatUsdt(o.sellingPriceUsdt)} USDT`} />
            <Info label="Attempts" value={String(o.attempts)} />
            <Info label="Payment reference" value={o.paymentReference ?? '—'} mono />
            <Info label="Payment method" value={o.paymentMethod === 'jazzcash' ? 'JazzCash' : o.paymentNetwork ?? (o.paymentReference ? 'Binance Pay' : '—')} />
          </div>

          {o.failureReason ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
              {o.failureReason}
            </div>
          ) : null}

          {o.status === 'verifying_payment' && o.paymentReference && !o.paymentNetwork ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="mb-2 font-medium text-amber-900">Verify this {o.paymentMethod === 'jazzcash' ? 'JazzCash transaction ID' : 'Binance Pay order ID'} before approving.</p>
              <p className="mb-3 font-mono text-xs text-amber-900">{o.paymentReference}</p>
              <div className="flex gap-2">
                <Button
                  loading={approvePayment.isPending}
                  onClick={() => approvePayment.mutate(o.id, { onSuccess: () => { toast({ title: 'Payment approved; order sent to supplier', variant: 'success' }); onClose(); }, onError: (e) => toast({ title: 'Approval failed', description: errorMessage(e), variant: 'error' }) })}
                >Approve payment</Button>
                <Button
                  variant="destructive"
                  loading={rejectPayment.isPending}
                  onClick={() => rejectPayment.mutate({ id: o.id, note: note || undefined }, { onSuccess: () => { toast({ title: 'Payment rejected', variant: 'success' }); onClose(); }, onError: (e) => toast({ title: 'Rejection failed', description: errorMessage(e), variant: 'error' }) })}
                >Reject</Button>
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-1 font-medium">Delivery data</p>
            <pre className="max-h-40 overflow-auto rounded-md border bg-muted/40 p-3 text-xs scrollbar-thin">
              {o.deliveryData ? JSON.stringify(o.deliveryData, null, 2) : '— none —'}
            </pre>
          </div>

          {canRecover ? (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium">Recover supplier delivery</p>
                  <p className="text-xs text-muted-foreground">Fetch the latest 50 supplier orders and explicitly assign the matching successful delivery.</p>
                </div>
                <Button variant="outline" loading={recovery.isPending} onClick={() => recovery.mutate(o.id, { onError: (e) => toast({ title: 'Could not fetch supplier orders', description: errorMessage(e), variant: 'error' }) })}>
                  Fetch supplier orders
                </Button>
              </div>
              {recovery.data ? (
                <div className="max-h-72 space-y-2 overflow-auto scrollbar-thin">
                  {recovery.data.map((candidate) => (
                    <div key={candidate.externalOrderId} className="flex items-center justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0">
                        <p className="font-medium">{candidate.serviceName || candidate.serviceId}</p>
                        <p className="font-mono text-xs">{candidate.externalOrderId}</p>
                        <p className="text-xs text-muted-foreground">{candidate.createdAt} · Qty {candidate.quantity} · {formatUsdt(candidate.amount)} USDT · {candidate.status} · {candidate.codes.length} delivery item(s)</p>
                      </div>
                      <Button
                        size="sm"
                        disabled={!candidate.codes.length || !['success', 'paid', 'delivered', 'completed'].includes(candidate.status.toLowerCase())}
                        loading={assignDelivery.isPending}
                        onClick={() => {
                          if (!confirm(`Assign delivery from ${candidate.externalOrderId} to this customer?`)) return;
                          assignDelivery.mutate({ id: o.id, supplierOrderId: candidate.externalOrderId }, {
                            onSuccess: () => { toast({ title: 'Delivery assigned and sent to customer', variant: 'success' }); onClose(); },
                            onError: (e) => toast({ title: 'Delivery assignment failed', description: errorMessage(e), variant: 'error' }),
                          });
                        }}
                      >Assign delivery</Button>
                    </div>
                  ))}
                  {recovery.data.length === 0 ? <p className="text-sm text-muted-foreground">No recent supplier orders found.</p> : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div>
            <p className="mb-1 font-medium">Webhook / poll log ({o.webhookPayloads.length})</p>
            <pre className="max-h-48 overflow-auto rounded-md border bg-muted/40 p-3 text-xs scrollbar-thin">
              {JSON.stringify(o.webhookPayloads, null, 2)}
            </pre>
          </div>

          {canRefund ? (
            <div className="flex items-end gap-2 border-t pt-4">
              <div className="flex-1">
                <Input placeholder="Refund note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <Button
                variant="destructive"
                loading={refund.isPending}
                onClick={() =>
                  refund.mutate(
                    { id: o.id, note: note || undefined },
                    {
                      onSuccess: () => {
                        toast({ title: 'Order refunded', variant: 'success' });
                        onClose();
                      },
                      onError: (e) => toast({ title: 'Refund failed', description: errorMessage(e), variant: 'error' }),
                    },
                  )
                }
              >
                Mark refunded
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function Info({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={mono ? 'font-mono text-xs' : ''}>{value}</p>
    </div>
  );
}
